'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { Auth, Hub } from 'aws-amplify';
import { HubPayload } from '@aws-amplify/core';
import { v4 as uuidv4 } from 'uuid';
import Cookies from 'js-cookie';

import '@/core/amplify';

import { importLoveveryReact } from '@/utils/helpers';

import { getLocalStorage, setLocalStorage } from '@/utils/localStorage';

import { USER_SUBSCRIPTIONS_KEY } from './SubscriptionContext';
import { useLocalStorage } from 'usehooks-ts';

enum AuthEvents {
  SIGN_IN = 'signIn',
  SIGN_OUT = 'signOut',
}

export interface AuthState {
  // Attributes are defined in Cognito.
  attributes: {
    'custom:account_manager_id': string;
    loveveryId: string;
    email: string;
    email_verified: boolean;
    family_name: string;
    name: string;
    sub: string; // Cognito-specific unique ID.
  };
}

interface AuthContextValue {
  user: AuthState | null;
  authLoading: boolean;
  signUp: (email: string, password: string) => Promise<boolean>;
  signIn: (email: string, password: string) => Promise<boolean>;
  signOut: () => Promise<{ error: null | unknown }>;
}

const getAuthStateFromLocalStorage = () => getLocalStorage('user');

// This function extracts only the relevant auth data from events.
const transformAuthEventData = (data: any): AuthState | null => {
  if (!data) {
    return null;
  }

  return {
    attributes: {
      ...data.attributes,
      loveveryId: data.attributes['custom:account_manager_id'],
    },
  };
};

const AuthContext = createContext({} as AuthContextValue);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: any) => {
  // Auth Context State
  const [authState, setAuthState] = useState<AuthState | null>(
    getAuthStateFromLocalStorage,
  );

  const [authLoading, setAuthLoading] = useState(true);
  const [userJwt, setUserJwt] = useLocalStorage<string | null>(
    'le-user-jwt',
    null,
  );

  const setAuthStateAndStorage = (newAuthState: AuthState | null) => {
    setLocalStorage('user', newAuthState);
    setAuthState(newAuthState);
  };

  // Auth Context Events
  const handleSignIn = useCallback((data: any) => {
    const user = transformAuthEventData(data);
    setAuthStateAndStorage(user);
  }, []);

  const handleSignOut = useCallback(() => {
    setAuthStateAndStorage(null);
    Cookies.remove(USER_SUBSCRIPTIONS_KEY, {
      path: '/',
      domain: process.env.NEXT_PUBLIC_VANITY_DOMAIN as string,
    });
  }, []);

  const handleAuthEvent = useCallback(
    (payload: HubPayload) => {
      const { event, data } = payload;
      switch (event) {
        default: {
          return;
        }
        case AuthEvents.SIGN_IN: {
          handleSignIn(data);
          return;
        }
        case AuthEvents.SIGN_OUT: {
          handleSignOut();
          return;
        }
      }
    },
    [handleSignIn, handleSignOut],
  );

  const getUser = useCallback(async () => {
    try {
      const { getAndCheckCognitoUser } = await importLoveveryReact();
      const data = await getAndCheckCognitoUser();
      const user = transformAuthEventData(data);
      setAuthStateAndStorage(user);
      setAuthLoading(false);
      return { error: null };
    } catch (error) {
      console.error(error);
      setAuthStateAndStorage(null);
      setAuthLoading(false);
      return { error };
    }
  }, []);

  const signUp = async (email: string, password: string) => {
    const username = uuidv4();
    const storeDomain = process.env.NEXT_PUBLIC_VANITY_DOMAIN as string;

    // bypass email verification for checkout sign-ups
    const clientMetadata = {
      auto_confirm: 'true',
      store_domain: storeDomain,
    };

    try {
      await Auth.signUp({
        username,
        password,
        attributes: {
          email,
        },
        clientMetadata,
      });

      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      await Auth.signIn(email, password);
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const signOut = async () => {
    try {
      await Auth.signOut();
      setAuthStateAndStorage(null);
      return { error: null };
    } catch (error) {
      console.error(error);
      return { error };
    }
  };

  interface CognitoSession {
    idToken: {
      jwtToken: string;
    };
  }

  const getUserJwt = async () => {
    try {
      const cognitoSession = await Auth.currentSession();
      if (!cognitoSession) return null;

      const {
        idToken: { jwtToken },
      } = cognitoSession as unknown as CognitoSession;

      // save jwt to cookie;
      fetch('/api/save-user-jwt', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userJwt: jwtToken,
        }),
      });

      return (cognitoSession as unknown as CognitoSession).idToken.jwtToken;
    } catch (e) {
      return null;
    }
  };

  useEffect(() => {
    const authEventListener = () => {
      Hub.listen('auth', (data) => handleAuthEvent(data.payload));
    };

    authEventListener();
    getUser();
  }, [handleAuthEvent, getUser]);

  useEffect(() => {
    const retrieveUserJwt = async () => {
      const userJwt = await getUserJwt();
      setUserJwt(userJwt);
    };

    retrieveUserJwt();
  }, []);

  // Auth Context Value
  const authContextValue = {
    user: authState,
    authLoading,
    signUp,
    signIn,
    signOut,
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export default useAuth;
